import { Messages } from '../../codegen/Localize';
import { ILocLinkifierModel, LocLinkify } from '../filters/LocLinkifierFilter';
import { CommonErrorCode, LoginErrorCodes, SiteSwitchErrorCodes, UserErrorCode } from '../helpers/ErrorCodes';
import { Module } from '../Module';
import { AuthenticationType } from '../../settings/types/IServerSettingsUnauthenticated';
import { ServerSettingsUnauthenticated } from '../../settings/services/ServerSettingsUnauthenticated';
import {getAuthenticationServiceInstance} from '../../authentication/services/AuthenticationService';
import {getSessionManagementServiceInstance} from '../../sessionManagement/SessionManagementService';

export class ErrorMessageHandler {
  private static tableauKnowledgeBaseUrl = 'https://kb.tableau.com';

  public getSignInErrorMessages(errorCode: number): ILocLinkifierModel[] {
    const isClientCertWithFallback = AuthenticationType.ClientCertificateWithFallback === getAuthenticationServiceInstance().authenticationType();
    const errorMessages: ILocLinkifierModel[] = [];
    const isOnline = ServerSettingsUnauthenticated.domainFamily() === 'external';
    const contactAdmin = isOnline ? Messages.contactSiteAdmin() : Messages.contactServerAdmin();

    switch (errorCode) {
      case CommonErrorCode.SiteNotFound:
        errorMessages.push(LocLinkify.linkifier(Messages.signInFailedSiteNotFound()));
        errorMessages.push(LocLinkify.linkifier(contactAdmin));
        break;
      case CommonErrorCode.SiteUserNotFound:
        errorMessages.push(LocLinkify.linkifier(Messages.signInFailedSiteUserNotFound()));
        errorMessages.push(isOnline ?
          LocLinkify.linkifier(null, [LocLinkify.link(
            this.getSignInFailedSiteUserNotFoundKbUrl(),
            Messages.knowledgeBase())]) :
          LocLinkify.linkifier(contactAdmin));
        break;
      case CommonErrorCode.SystemUserNotFound:
        errorMessages.push(LocLinkify.linkifier(Messages.signInFailedSystemUserNotFound()));
        errorMessages.push(LocLinkify.linkifier(contactAdmin));
        break;
      case UserErrorCode.UserOpenIdUserNotFound:
        errorMessages.push(LocLinkify.linkifier(Messages.errorUserNotFound()));
        errorMessages.push(LocLinkify.linkifier(contactAdmin));
        break;
      case UserErrorCode.UserOpenIdConflictingUserAccount:
        errorMessages.push(LocLinkify.linkifier(Messages.conflictingUserAccount()));
        errorMessages.push(LocLinkify.linkifier(contactAdmin));
        break;
      case SiteSwitchErrorCodes.UserUnlicensedForSite:
        errorMessages.push(LocLinkify.linkifier(Messages.signInFailedUserUnlicensedForSite()));
        errorMessages.push(LocLinkify.linkifier(contactAdmin));
        break;
      case SiteSwitchErrorCodes.SiteLocked:
        errorMessages.push(LocLinkify.linkifier(Messages.signInFailedSiteLocked()));
        errorMessages.push(LocLinkify.linkifier(contactAdmin));
        break;
      case SiteSwitchErrorCodes.SiteSuspended:
        errorMessages.push(LocLinkify.linkifier(Messages.signInFailedSiteSuspended()));
        break;
      case LoginErrorCodes.ClientCertificateNotFound:
        errorMessages.push(LocLinkify.linkifier(Messages.signinFailedClientCertificateNotFound()));
        errorMessages.push(LocLinkify.linkifier(isClientCertWithFallback ?
          Messages.signInUsingTableauServerCredentialsOption() : contactAdmin));
        break;
      case LoginErrorCodes.ClientCertificateUserNotFoundInCertificate:
        errorMessages.push(LocLinkify.linkifier(Messages.signinFailedClientCertificateUserNotFoundInCertificate()));
        errorMessages.push(LocLinkify.linkifier(isClientCertWithFallback ?
          Messages.signInUsingTableauServerCredentialsOption() : contactAdmin));
        break;
      case LoginErrorCodes.ClientCertificateUserNotFoundOnServer:
        errorMessages.push(LocLinkify.linkifier(Messages.signinFailedClientCertificateUserNotFoundOnServer()));
        errorMessages.push(LocLinkify.linkifier(isClientCertWithFallback ?
          Messages.signInUsingTableauServerCredentialsOption() : contactAdmin));
        break;
      case LoginErrorCodes.KerberosFailed:
      case LoginErrorCodes.SspiFailed:
        errorMessages.push(LocLinkify.linkifier(Messages.signInFailedAutoSignin()));
        errorMessages.push(LocLinkify.linkifier(Messages.signInUsingTableauServerCredentials()));
        break;
      case LoginErrorCodes.BadCredentials:
        errorMessages.push(LocLinkify.linkifier(Messages.signInFailedBadCredentials()));
        break;
      case CommonErrorCode.InvalidSession:
        errorMessages.push(LocLinkify.linkifier(Messages.sessionExpired()));
        break;
      case CommonErrorCode.UnlicensedServer:
        errorMessages.push(LocLinkify.linkifier(Messages.signInFailedUnlicensedServer()));
        errorMessages.push(LocLinkify.linkifier(contactAdmin));
        break;
      case UserErrorCode.UserOpenIdIdpNotReachable:
        errorMessages.push(LocLinkify.linkifier(Messages.cannotReachOpenIdIdp()));
        errorMessages.push(LocLinkify.linkifier(contactAdmin));
        break;
      case UserErrorCode.UserOpenIdBadConfig:
        errorMessages.push(LocLinkify.linkifier(Messages.openIdBadConfig()));
        errorMessages.push(LocLinkify.linkifier(contactAdmin));
        break;
      case UserErrorCode.UserInvalidPasswordResetToken:
        errorMessages.push(LocLinkify.linkifier(Messages.invalidPasswordResetLink()));
        break;
      case UserErrorCode.UserPasswordResetTokenExpired:
        errorMessages.push(LocLinkify.linkifier(Messages.linkExpired()));
        break;
      case LoginErrorCodes.OpenIdFailed:
        errorMessages.push(LocLinkify.linkifier(Messages.signInFailedUnknownReason()));
        errorMessages.push(LocLinkify.linkifier(contactAdmin));
        break;
      case UserErrorCode.UserPasswordNotRespectingPolicies:
        errorMessages.push(LocLinkify.linkifier(Messages.errorUserPasswordNotRespectingPolicies()));
        break;
      case UserErrorCode.LoginAttemptRejected:
        errorMessages.push(LocLinkify.linkifier(Messages.errorAccountIsInLockoutPeriod()));
        break;
      case UserErrorCode.AccountIsBlocked:
        errorMessages.push(LocLinkify.linkifier(Messages.errorAccountIsBlocked()));
        errorMessages.push(LocLinkify.linkifier(contactAdmin));
        break;
      default:
        errorMessages.push(LocLinkify.linkifier(Messages.signInFailedUnknownReason()));
        break;
    }
    return errorMessages;
  }

  private getSignInFailedSiteUserNotFoundKbUrl() {
    const kbPageId = 'error-errorcode-4-this-user-account-is-not-active-for-help-contact-your-tableau-online-administrator-signing-into-tableau-online';
    return this.getKbArticleUrl(kbPageId);
  }

  private getKbArticleUrl(pageId: string): string {
    const lang = getSessionManagementServiceInstance().sessionLanguage();
    const langQuery = lang ? '?lang=' + lang : '';
    return ErrorMessageHandler.tableauKnowledgeBaseUrl + '/articles/Issue/' + pageId + langQuery;
  }
}

Module.instance().service('ErrorMessageHandler', ErrorMessageHandler);
